/**
 * Asks the service worker whether the deployed app has changed. Nothing else, and nothing blocking.
 *
 * It used to wait for the new worker to take control before letting a menu click through, on a
 * `controllerchange` that may never come while the worker is still downloading its precache (~3 min for
 * 5 MB on a field connection): that is what froze the app. The new build now announces itself when it
 * is installed and in control, and `index.tsx` offers the reload.
 */

// One check per half minute: the browser byte-compares /service-worker.js on every call, and a click
// on five menu entries in a row used to fire five of them.
const CHECK_INTERVAL_MS = 30_000;

let lastCheckAt = 0;

// A full storage quota is what stops a new worker from installing: workbox precaches ~5 MB, the browser
// throws `QuotaExceededError`, the old build stays and every attempt retries. The photo and API caches
// are refilled by `NetworkFirst` on demand, the app itself cannot be: when the origin is nearly full,
// drop them first so the precache has room.
const FREE_SPACE_RATIO = 0.9;

// The API photo has the priority: the offline copy is a compressed version of it for offline use
const REFILLABLE_CACHES = ['data-cache'];
const LAST_RESORT_CACHES = ['image-offline-cache', 'image-cache'];

const isNearlyFull = async (): Promise<boolean> => {
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();

  return !!quota && usage / quota >= FREE_SPACE_RATIO;
};

async function freeSpaceIfNeeded(): Promise<void> {
  if (!navigator.storage?.estimate) {
    return;
  }

  try {
    if (!(await isNearlyFull())) {
      return;
    }

    console.warn('TreeWallet: storage nearly full, clearing the caches that refill themselves');
    await Promise.all(REFILLABLE_CACHES.map((name) => caches.delete(name)));

    if (!(await isNearlyFull())) {
      return;
    }

    console.warn('TreeWallet: still nearly full, dropping the offline photos too');
    await Promise.all(LAST_RESORT_CACHES.map((name) => caches.delete(name)));
  } catch (error) {
    console.debug('TreeWallet: could not check the storage estimate', error);
  }
}

/**
 * Starts a background check and returns `false` at once: navigation is never the update's business.
 * The prompt appears by itself when the new build is ready (see `index.tsx`).
 */
export function updateIfAvailable(): Promise<boolean> {
  void checkForUpdate();
  return Promise.resolve(false);
}

async function checkForUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator) || Date.now() - lastCheckAt < CHECK_INTERVAL_MS) {
    return;
  }

  lastCheckAt = Date.now();
  await freeSpaceIfNeeded();

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    // Fetches /service-worker.js and byte-compares it: a new build is what makes it differ
    await registration?.update();
  } catch (error) {
    console.debug('TreeWallet: the update check failed', error);
  }
}
